import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Music, Mic, Radio, Beer, Gamepad2, Trophy, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_ICONS = {
  karaoke: { icon: Mic, color: 'text-pink-400', bg: 'bg-pink-500/20' },
  live_music: { icon: Music, color: 'text-purple-400', bg: 'bg-purple-500/20' },
  dj: { icon: Radio, color: 'text-blue-400', bg: 'bg-blue-500/20' },
  happy_hour: { icon: Beer, color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
  trivia: { icon: Gamepad2, color: 'text-green-400', bg: 'bg-green-500/20' },
  sports: { icon: Trophy, color: 'text-orange-400', bg: 'bg-orange-500/20' },
  special: { icon: Sparkles, color: 'text-cyan-400', bg: 'bg-cyan-500/20' },
  other: { icon: Calendar, color: 'text-slate-400', bg: 'bg-slate-500/20' }
};

const EventCard = ({ event }) => {
  const eventStyle = EVENT_ICONS[event.event_type] || EVENT_ICONS.other;
  const IconComponent = eventStyle.icon;
  
  return (
    <div className={`p-3 rounded-lg ${eventStyle.bg} border border-slate-700/50`}>
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg bg-slate-800/50 ${eventStyle.color}`}>
          <IconComponent className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-semibold truncate">{event.title}</h4>
          {event.bar && (
            <p className="text-sm text-slate-400 truncate">{event.bar.name}</p>
          )}
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-slate-300">
              {event.start_time}{event.end_time && ` - ${event.end_time}`}
            </span>
            <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
              {event.event_type.replace('_', ' ')}
            </Badge>
          </div>
          {event.description && (
            <p className="text-xs text-slate-400 mt-1 line-clamp-2">{event.description}</p>
          )}
        </div>
      </div>
    </div>
  );
};

const TonightEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/events/tonight`);
        setEvents(response.data);
      } catch (error) {
        console.error('Failed to fetch events:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  if (loading) {
    return (
      <Card className="glass p-4 border border-slate-700">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-slate-700 rounded w-1/3"></div>
          <div className="h-20 bg-slate-700 rounded"></div>
        </div>
      </Card>
    );
  }

  if (events.length === 0) {
    return null; // Don't show section if no events
  }

  return (
    <Card className="glass p-4 border border-slate-700">
      <div className="flex items-center gap-2 mb-4">
        <Calendar className="w-5 h-5 text-purple-400" />
        <h3 className="text-lg font-semibold text-white">Tonight's Events</h3>
        <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/50">
          {events.length} events
        </Badge>
      </div>
      <div className="space-y-3 max-h-80 overflow-y-auto">
        {events.map(event => (
          <EventCard key={event.id} event={event} />
        ))}
      </div>
    </Card>
  );
};

const BarEvents = ({ barId, barName }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/bars/${barId}/events`);
        setEvents(response.data);
      } catch (error) {
        console.error('Failed to fetch bar events:', error);
      } finally {
        setLoading(false);
      }
    };
    if (barId) fetchEvents();
  }, [barId]);

  if (loading) {
    return (
      <div className="animate-pulse h-20 bg-slate-700/50 rounded-lg"></div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="text-center py-4 text-slate-400">
        <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No upcoming events</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {events.slice(0, 5).map(event => (
        <EventCard key={event.id} event={{ ...event, bar: { name: barName } }} />
      ))}
    </div>
  );
};

export { TonightEvents, BarEvents, EventCard };
export default TonightEvents;
